import clsx from "clsx";
import { type ComponentProps, Fragment, type JSX, useState } from "react";

import type { MobileSelectionListItem } from "common/components/MobileSelectionList/index";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import { typedObjectEntries } from "common/helpers/utils";

import { Tag } from "../ui/Tag";
import TagSelectionDrawer from "./TagSelectionDrawer";

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

export interface MobileSelectionListProps<
  DataType extends { id: string },
  ItemKey extends string,
> {
  title: string;
  data: DataType[];
  selection: DataType[];
  totalDataCount: number;
  children: (
    item: DataType,
    listItemProps: Pick<
      ComponentProps<typeof MobileSelectionListItem>,
      "isSelected" | "isSelecting" | "toggleIsSelected" | "badgeContent"
    >,
    selectedItems: ItemKey[],
  ) => JSX.Element;
  onSelectionChange?: (selection: "all" | DataType[]) => void;
  allItems?: Record<
    ItemKey,
    {
      icon: Icon;
      isDefault?: true;
      label: string;
      /** Returning nothing skips the tag, for values rendered by the list item. */
      renderTag?: (data: DataType, isDesktop: false) => ReactNode;
    }
  >;
  canSelectAll?: boolean;
}

export default function MobileSelectionList<
  DataType extends { id: string },
  ItemKey extends string,
>({
  title,
  data,
  selection,
  totalDataCount,
  children,
  onSelectionChange,
  allItems,
  canSelectAll = true,
}: MobileSelectionListProps<DataType, ItemKey>) {
  const isSelectionEnabled = onSelectionChange !== undefined;
  const [isSelecting, setIsSelecting] = useState(false);
  const [visibleItemKeys, setVisibleItemKeys] = useState<ItemKey[]>(() =>
    allItems
      ? typedObjectEntries(allItems)
          .filter(([, { isDefault }]) => isDefault)
          .map(([key]) => key)
      : [],
  );

  const isSelectingMode = isSelectionEnabled && isSelecting;
  const hasSelection = !!selection.length;
  //true if all the items in the current page are selected
  const isAllSelected = selection.length === totalDataCount;

  let checkboxState: boolean | "indeterminate" = false;
  if (isAllSelected) {
    checkboxState = true;
  } else if (hasSelection) {
    checkboxState = "indeterminate";
  }

  return (
    <div className="flex flex-col -mx-2">
      <div className="border-y border-y-black-200 p-2 gap-x-1.5 flex items-center mb-1">
        <RenderIf condition={isSelectingMode}>
          <Checkbox
            checked={checkboxState}
            onCheckedChange={() => {
              if (hasSelection) {
                onSelectionChange([]);
              } else {
                onSelectionChange(data);
              }
            }}
          />
        </RenderIf>

        <div className="flex flex-row gap-1.5 items-center text-button-xs">
          {isSelectingMode ? (
            <span className="text-black-500">
              <span className={selection.length && "text-black-950"}>
                {selection.length}
              </span>{" "}
              of {totalDataCount}
            </span>
          ) : (
            <>
              <span className="text-black-950">{title}</span>

              <Tag variant="tertiary" size="sm">
                <span className="text-black-500">{totalDataCount}</span>
              </Tag>
            </>
          )}
        </div>

        <RenderIf condition={isSelectionEnabled && hasSelection}>
          {selection.length === totalDataCount ? (
            <Button
              variant="link-purple"
              size="sm"
              onClick={() => onSelectionChange([])}
            >
              Clear selection
            </Button>
          ) : (
            <RenderIf condition={canSelectAll}>
              <Button
                variant="link-purple"
                size="sm"
                onClick={() => onSelectionChange("all")}
              >
                Select all
              </Button>
            </RenderIf>
          )}
        </RenderIf>
        <div className="flex ml-auto gap-x-1.5">
          <RenderIf condition={isSelectionEnabled}>
            <Button
              variant="secondary-purple"
              size="sm"
              onClick={() => {
                if (isSelecting) {
                  onSelectionChange([]);
                }
                setIsSelecting(!isSelecting);
              }}
            >
              {isSelecting ? "Cancel" : "Select"}
            </Button>
          </RenderIf>
          {allItems && (
            <TagSelectionDrawer
              title={title}
              itemKeys={visibleItemKeys}
              setItemKeys={setVisibleItemKeys}
              allItems={allItems}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        {data.map((item, index) => {
          const isSelected = !!selection.find(
            (selectionItem) => selectionItem.id === item.id,
          );
          const nextItem = data[index + 1];
          const isNextSelected =
            nextItem &&
            selection.find((selectionItem) => selectionItem.id === nextItem.id);

          return (
            <Fragment key={item ? item.id : index}>
              {children(
                item,
                {
                  isSelecting: isSelectingMode,
                  isSelected,
                  toggleIsSelected: () => {
                    if (isSelected) {
                      onSelectionChange?.(
                        selection.filter(
                          (selectedItem) => selectedItem.id !== item.id,
                        ),
                      );
                    } else {
                      onSelectionChange?.([...selection, item]);
                    }
                  },
                  badgeContent:
                    allItems && item
                      ? visibleItemKeys.flatMap((key) => {
                          const rendered = allItems[key].renderTag?.(
                            item,
                            false,
                          );
                          return rendered
                            ? [<Fragment key={key}>{rendered}</Fragment>]
                            : [];
                        })
                      : [],
                },
                visibleItemKeys,
              )}
              <div
                className={clsx(
                  "h-px w-full bg-black-200 mb-1 last:mb-0",
                  (isSelected || isNextSelected) && "invisible",
                )}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
